import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { sendBioMessage } from "@/services/bioLeadService";
import "./BioMessageSheet.scss";

const EMPTY = { name: "", email: "", message: "", website: "" };

export const replyOf = (error) => {
  const data = error?.response?.data || {};
  return {
    errors: data?.result?.errors || {},
    message: data?.message || "We couldn't send that. Check your connection and try again.",
  };
};

const BioMessageSheet = ({ show, onHide, slug, agentFirstName }) => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [problem, setProblem] = useState("");
  const [state, setState] = useState("compose");
  const [sentTo, setSentTo] = useState("");
  const openedAt = useRef(Date.now());
  const name = agentFirstName || "this agent";

  useEffect(() => {
    if (show) {
      openedAt.current = Date.now();
      setState((current) => (current === "sent" ? "sent" : "compose"));
    }
  }, [show]);

  const update = (key) => (event) => {
    setValues((current) => ({ ...current, [key]: event.target.value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setState("sending");
    setProblem("");
    try {
      const response = await sendBioMessage(slug, { ...values, elapsedMs: Date.now() - openedAt.current });
      setSentTo(response?.result?.email || values.email.trim());
      setState("sent");
    } catch (error) {
      const reply = replyOf(error);
      setErrors(reply.errors);
      setProblem(Object.keys(reply.errors).length ? "" : reply.message);
      setState("compose");
    }
  };

  const close = () => {
    if (state === "sent") {
      setValues(EMPTY);
      setState("compose");
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={close} centered className="bio-message-sheet" aria-labelledby="bio-message-title">
      <Modal.Header closeButton>
        <Modal.Title id="bio-message-title">{state === "sent" ? "Check your email" : `Message ${name}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {state === "sent" ? (
          <div className="bio-message-sheet__sent" role="status">
            <p>
              We sent a link to <strong>{sentTo}</strong>. Tap it and your message goes to {name}.
            </p>
            <p className="bio-message-sheet__hint">
              The link works for 24 hours. It also creates your free Gitcha account, so you can read {name}&apos;s reply.
            </p>
            <div className="bio-message-sheet__actions">
              <button type="button" className="btn btn-link p-0" onClick={() => setState("compose")}>
                Wrong email? Change it
              </button>
              <button type="button" className="btn btn-primary" onClick={close}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form className="bio-message-sheet__form" onSubmit={submit} noValidate>
            <p className="bio-message-sheet__lead">
              {name} gets your message after you confirm your email. Your email is only shared with {name}.
            </p>
            <label htmlFor="bio-message-name">Your name</label>
            <input id="bio-message-name" className="form-control" autoComplete="name" value={values.name} onChange={update("name")} aria-invalid={Boolean(errors.name)} />
            {errors.name ? <p className="bio-message-sheet__error">{errors.name}</p> : null}
            <label htmlFor="bio-message-email">Email</label>
            <input id="bio-message-email" type="email" className="form-control" autoComplete="email" value={values.email} onChange={update("email")} aria-invalid={Boolean(errors.email)} />
            {errors.email ? <p className="bio-message-sheet__error">{errors.email}</p> : null}
            <label htmlFor="bio-message-text">Message</label>
            <textarea
              id="bio-message-text"
              className="form-control"
              rows={5}
              placeholder={`Hi ${name}, we're looking for a home in...`}
              value={values.message}
              onChange={update("message")}
              aria-invalid={Boolean(errors.message)}
            />
            {errors.message ? <p className="bio-message-sheet__error">{errors.message}</p> : null}
            <input className="bio-message-sheet__trap" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={values.website} onChange={update("website")} />
            {problem ? <p className="bio-message-sheet__problem" role="alert">{problem}</p> : null}
            <button type="submit" className="btn btn-primary w-100" disabled={state === "sending"}>
              {state === "sending" ? "Sending..." : "Send message"}
            </button>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default BioMessageSheet;
